import { useCallback, useEffect, useState } from 'react';

const PHONE_STORAGE_KEY = 'joviaa_checkout_phone';

function loadSavedPhone(): string {
  try {
    return localStorage.getItem(PHONE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function savePhone(phone: string) {
  try {
    if (phone.trim().length >= 10) {
      localStorage.setItem(PHONE_STORAGE_KEY, phone.trim());
    }
  } catch {
    // Storage unavailable
  }
}

export const PHONE_REGEX = /^[6-9]\d{9}$/;

export function validatePhone(phone: string): { valid: boolean; error: string } {
  const cleaned = phone.replace(/\s|-/g, '').trim();

  if (!cleaned) {
    return { valid: false, error: 'Phone number is required for order confirmation.' };
  }

  // Strip +91 prefix if present
  const withoutCode = cleaned.startsWith('+91')
    ? cleaned.slice(3)
    : cleaned.startsWith('91') && cleaned.length === 12
      ? cleaned.slice(2)
      : cleaned;

  if (!PHONE_REGEX.test(withoutCode)) {
    if (withoutCode.length < 10) {
      return { valid: false, error: 'Phone number must be exactly 10 digits.' };
    }
    if (withoutCode.length > 10) {
      return { valid: false, error: 'Phone number seems too long — expected 10 digits.' };
    }
    return { valid: false, error: 'Enter a valid Indian mobile number starting with 6-9.' };
  }

  return { valid: true, error: '' };
}

export function cleanPhone(phone: string): string {
  const cleaned = phone.replace(/\s|-/g, '').trim();
  if (cleaned.startsWith('+91')) return cleaned.slice(3);
  if (cleaned.startsWith('91') && cleaned.length === 12) return cleaned.slice(2);
  return cleaned;
}

export function usePhoneValidation() {
  const [phone, setPhone] = useState(loadSavedPhone);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState(false);

  // Validate whenever phone changes (after first touch)
  useEffect(() => {
    if (!touched) return;
    const result = validatePhone(phone);
    setError(result.error);
  }, [phone, touched]);

  const handlePhoneChange = useCallback((value: string) => {
    // Allow only digits, spaces, hyphens, and leading +
    const filtered = value.replace(/[^\d\s\-+]/g, '');
    setPhone(filtered);
  }, []);

  const handleBlur = useCallback(() => {
    setTouched(true);
  }, []);

  const isValid = touched && validatePhone(phone).valid;

  const persistPhone = useCallback(() => {
    if (validatePhone(phone).valid) {
      savePhone(phone);
    }
  }, [phone]);

  return {
    phone,
    error,
    touched,
    isValid,
    handlePhoneChange,
    handleBlur,
    persistPhone,
    displayPhone: cleanPhone(phone),
  };
}